/**
 * THE SEED, AS A THING A PLAYER TYPES.
 *
 * Howard, 17 Aug 2026, about the Career screen's seed field:
 *
 *   "this screen ought to have a seed randomizer button instead of increments."
 *
 * He is right, and the reason is worth writing down: a seed is a NAME, not a
 * quantity. Nudging 424242 up to 424243 with a browser stepper does not give
 * you "a slightly different league", it gives you an unrelated one — the number
 * is hashed before anything is dealt from it. Arrows imply an ordering the
 * value does not have, so they are removed and replaced with the two operations
 * that ARE meaningful:
 *
 *   • ROLL A NEW ONE  — `randomSeed`, for "give me a league I have not seen".
 *   • TYPE AN OLD ONE — `parseSeed`, for "give me back the league I had".
 *
 * The second is the entire point of seeding this project. Every screen that
 * shows a seed must let it be typed or pasted; a read-only seed with a dice
 * button beside it would be a random-number generator wearing a seed's clothes.
 *
 * PURE. No React, no storage, no `Math.random` unless a caller declines to pass
 * one — which is why `randomSeed` takes its randomness as an argument and the
 * tests can pin both ends of its range exactly.
 */

/**
 * The range `randomSeed` draws from: 0 … 999,999,999.
 *
 * Chosen to match what the Race Week screen's "Randomise" button has always
 * produced (`Math.floor(Math.random() * 1_000_000_000)`), so this refactor does
 * not quietly change which leagues that button can reach. It sits comfortably
 * inside the 32-bit space every generator downstream folds a seed into, so a
 * rolled seed is never one of the values that alias onto another.
 */
export const MAX_SEED_EXCLUSIVE = 1_000_000_000;

/**
 * The largest seed that can be TYPED and still mean itself.
 *
 * `makeRng`/`Dice` and `buildSchedule` all fold the seed through `>>> 0`, so
 * 4,294,967,296 and 0 are the same league. Rather than accept a number and
 * silently race a different season than the one printed on the screen, anything
 * above the 32-bit ceiling is refused with a sentence saying why.
 */
export const MAX_TYPED_SEED = 0xffff_ffff;

/**
 * A fresh seed. `random` is injectable so the range can be pinned at both ends
 * in a test rather than sampled and hoped about.
 *
 * Returns an integer in [0, MAX_SEED_EXCLUSIVE). `Math.random()` never returns
 * 1, so the top value is MAX_SEED_EXCLUSIVE - 1; the `Math.min` is a rail
 * against an injected generator that does not honour that contract, because a
 * seed of exactly 1e9 is still a legal seed and silently producing one out of
 * the documented range would make this function's own docs a lie.
 */
export function randomSeed(random: () => number = Math.random): number {
  const raw = Math.floor(random() * MAX_SEED_EXCLUSIVE);
  if (!Number.isFinite(raw) || raw < 0) return 0;
  return Math.min(raw, MAX_SEED_EXCLUSIVE - 1);
}

export interface SeedReading {
  /** The seed, when the text is one. Null when it is not. */
  seed: number | null;
  /**
   * Why it is not, written for the person at the keyboard. Null when it is.
   * Never a bare "invalid": a player who pasted a seed with a stray character
   * needs to be told which rule they broke, not that they broke one.
   */
  problem: string | null;
}

/**
 * Read a typed or pasted seed.
 *
 * DELIBERATELY STRICT about what a seed looks like — digits, with whitespace
 * around them tolerated because pasting from a chat message brings some. Commas
 * and spaces INSIDE the number are refused rather than stripped: "424,242" and
 * "424242" would be the same league under a stripping rule, but so would
 * "42 42 42" and "424242", and a player who mistypes a group separator would
 * get a league they did not ask for with no way to tell.
 *
 * An empty field is not an error — it is a field being retyped — so it reports
 * no seed and no problem, and the caller keeps the seed it already had.
 */
export function parseSeed(text: string): SeedReading {
  const trimmed = text.trim();
  if (trimmed === '') return { seed: null, problem: null };
  if (!/^\d+$/.test(trimmed)) {
    return {
      seed: null,
      problem: `"${trimmed}" is not a seed — a seed is whole digits only, with no signs, spaces or separators.`,
    };
  }
  const value = Number(trimmed);
  if (value > MAX_TYPED_SEED) {
    return {
      seed: null,
      problem:
        `${trimmed} is above the largest seed this game can hold (${MAX_TYPED_SEED.toLocaleString('en-US')}). ` +
        'Bigger numbers wrap around onto smaller ones, so the league you got back would not be the one printed on the screen.',
    };
  }
  return { seed: value, problem: null };
}
